import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { parseLocalDate } from "../../../../common/service/CommonService";
import { ContainerItem, CoaDetailRow, IpInventoryFormValues } from "./ipInventory.types";

/**
 * Custom hook to manage stepper currentStep state with URL query param persistence (?step=N)
 */
export const useIpStepPersistence = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stepFromUrl = Number(searchParams.get("step")) || 1;
  const [currentStep, setCurrentStep] = useState<number>(stepFromUrl);

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", String(step));
        return next;
      },
      { replace: true }
    );
  };

  return {
    currentStep,
    setCurrentStep,
    handleStepChange,
    searchParams,
    setSearchParams,
  };
};

/**
 * Parse human-readable file size string to numeric bytes
 */
export const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;

  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val;
};

/**
 * Calculate dynamic min/max date restrictions for form fields
 */
export const calculateDateRestrictions = (
  selectedmanufacturingdate?: string | null,
  selecteddateofreceipt?: string | null,
  selectedexpirydate?: string | null,
  selectedquarantinestartdate?: string | null
) => {
  return {
    manufacturingdate: {
      maxDate: "today",
    },
    dateofreceipt: {
      minDate: parseLocalDate(selectedmanufacturingdate),
      maxDate: parseLocalDate(selectedexpirydate),
    },
    expirydate: {
      minDate: parseLocalDate(selectedmanufacturingdate),
    },
    coareceiptdate: {
      minDate: parseLocalDate(selecteddateofreceipt),
    },
    quarantinestartdate: {
      minDate: parseLocalDate(selecteddateofreceipt),
    },
    quarantineenddate: {
      minDate: parseLocalDate(selectedquarantinestartdate),
    },
  };
};

/**
 * Helper to determine if a form field should be disabled based on study selection and parent date values
 */
export const checkIsFieldDisabled = (
  fieldName:
    | "regulatoryofstudy"
    | "productname"
    | "genericname"
    | "strength"
    | "dosageform"
    | "labelclaim"
    | "lightcondition"
    | "batchlotno"
    | "batchorlot"
    | "manufacturerlabel"
    | "manufacturername"
    | "manufacturingdate"
    | "typeofdate"
    | "expirydate"
    | "unit"
    | "storagecondition"
    | "dateofreceipt"
    | "coareceiptdate"
    | "quarantinestartdate"
    | "quarantineenddate",
  selectedStudyCode?: string,
  selectedmanufacturingdate?: string | null,
  selectedexpirydate?: string | null,
  selecteddateofreceipt?: string | null,
  selectedquarantinestartdate?: string | null
): boolean => {
  if (!selectedStudyCode) return true;

  if (fieldName === "expirydate") {
    return !selectedmanufacturingdate;
  }
  if (fieldName === "dateofreceipt") {
    return !selectedmanufacturingdate || !selectedexpirydate;
  }
  if (fieldName === "coareceiptdate") {
    return !selecteddateofreceipt;
  }
  if (fieldName === "quarantinestartdate") {
    return !selecteddateofreceipt;
  }
  if (fieldName === "quarantineenddate") {
    return !selectedquarantinestartdate;
  }

  return false;
};

/**
 * Format COA Details table list for API payload submission
 */
export const formatCoaDetails = (coaList: CoaDetailRow[]) => {
  return coaList.map((item) => ({
    ...(item.coaid ? { coaid: item.coaid } : {}),
    checklistquestion: item.particulars,
    coaanswer: item.coaInfo,
    labelanswer: item.labelInfo,
  }));
};

/**
 * Format only modified/changed COA Details table items for Update API payload submission
 */
export const formatModifiedCoaDetails = (
  coaList: CoaDetailRow[],
  originalCoaList: CoaDetailRow[] = []
) => {
  if (!originalCoaList || originalCoaList.length === 0) {
    return formatCoaDetails(coaList);
  }

  const modifiedItems = coaList.filter((item) => {
    const originalMatch = originalCoaList.find(
      (orig) =>
        (orig.coaid && item.coaid && String(orig.coaid) === String(item.coaid)) ||
        orig.particulars === item.particulars
    );
    if (!originalMatch) return true;
    return (
      item.coaInfo !== originalMatch.coaInfo ||
      item.labelInfo !== originalMatch.labelInfo
    );
  });

  return formatCoaDetails(modifiedItems);
};

/**
 * Build Insert mutation payload
 */
export const buildInsertPayload = (
  data: IpInventoryFormValues,
  selectedStudy: any,
  containersList: ContainerItem[],
  totalContainerQty: number,
  coaList: CoaDetailRow[],
  uploadedFiles: File[]
): IpInventoryFormValues => {
  const newContainersList = containersList.filter((c: any) => !c.isExisting);
  const containers = newContainersList.map((c, idx) => ({
    containernumber: c.containernumber || String(idx + 1).padStart(2, "0"),
    initialquantity: Number(c.initialquantity) || 0,
    containertype: c.containertype || null,
    containercondition: c.containercondition || null,
    containerremarks: c.containerremarks || null,
    isusable: c.isusable !== false,
  }));

  const payload: IpInventoryFormValues = {
    ...data,
    studyid: selectedStudy?.studyid,
    manufacturingdate: data.manufacturingdate,
    expirydate: data.expirydate,
    dateofreceipt: data.dateofreceipt,
    receivedqty: totalContainerQty > 0 ? totalContainerQty : Number(data.receivedqty),
    totalcontainers: newContainersList.length > 0 ? newContainersList.length : Number(data.totalcontainers),
    containers,
    coadetails: formatCoaDetails(coaList) as any,
    file: uploadedFiles,
  };

  const isPrintNoOrNa = data.loggerprintobtained === "no" || data.loggerprintobtained === "na";
  const isTempNoOrNa = data.tempexcursionacceptable === "no" || data.tempexcursionacceptable === "na";

  if (String(data.isdatalogger) === "false") {
    payload.dataloggerid = null;
    payload.loggerprintobtained = null;
    payload.tempexcursionacceptable = null;
  } else if (!isPrintNoOrNa && !isTempNoOrNa) {
    payload.dataloggerremarks = null;
  }

  return payload;
};

/**
 * Build Update mutation payload
 */
export const buildUpdatePayload = (
  formData: IpInventoryFormValues,
  editId: string | number,
  reason: string,
  containersList: ContainerItem[],
  originalContainers: any[],
  totalContainerQty: number,
  coaList: CoaDetailRow[],
  originalCoaList: CoaDetailRow[] = [],
  deletedAttachmentIds: number[],
  uploadedFiles: File[]
): any => {
  // 1. Map current containers list to Action: 'U' (updated) or 'I' (inserted)
  const currentContainersMapped = containersList.map((c) => {
    const originalMatch = c.ipdetailid
      ? originalContainers.find((orig: any) => String(orig.ipdetailid) === String(c.ipdetailid))
      : null;
    return {
      ...(originalMatch || {}),
      containernumber: c.containernumber,
      initialquantity: Number(c.initialquantity) || 0,
      containertype: c.containertype || null,
      containercondition: c.containercondition || null,
      containerremarks: c.containerremarks || null,
      isusable: c.isusable !== false,
      action: originalMatch ? "U" : "I",
    };
  });

  // 2. Identify deleted containers and map to Action: 'D' (deleted)
  const deletedContainersMapped = originalContainers
    .filter((orig: any) =>
      !containersList.some((c) => c.ipdetailid && String(c.ipdetailid) === String(orig.ipdetailid))
    )
    .map((orig: any) => ({
      ...orig,
      initialquantity: Number(orig.initialquantity) || 0,
      action: "D",
    }));

  const containers = [...currentContainersMapped, ...deletedContainersMapped];

  const payload: any = {
    ...formData,
    ipid: editId,
    updatereason: reason,
    manufacturingdate: formData.manufacturingdate,
    expirydate: formData.expirydate,
    dateofreceipt: formData.dateofreceipt,
    receivedqty: totalContainerQty > 0 ? totalContainerQty : Number(formData.receivedqty),
    totalcontainers: Number(formData.totalcontainers),
    containers,
    coadetails: formatModifiedCoaDetails(coaList, originalCoaList),
    deletedAttachmentIds: deletedAttachmentIds,
    file: uploadedFiles,
  };

  const isPrintNoOrNa = formData.loggerprintobtained === "no" || formData.loggerprintobtained === "na";
  const isTempNoOrNa = formData.tempexcursionacceptable === "no" || formData.tempexcursionacceptable === "na";

  if (String(formData.isdatalogger) === "false") {
    payload.dataloggerid = null;
    payload.loggerprintobtained = null;
    payload.tempexcursionacceptable = null;
  } else if (!isPrintNoOrNa && !isTempNoOrNa) {
    payload.dataloggerremarks = null;
  }

  return payload;
};
